import { type MouseEvent, useMemo, useState } from 'react';

import { ChartRendererProps } from '../../types/chart-renderer-props';
import { WAITING_DAYS_DONUT_LABELS, toWaitingDaysDonutChart } from '../../utils/to-waiting-days-donut-chart';
import { WAITING_DAYS_STACKED_BAR_COLORS } from '../shared/waiting-days-chart-colors';

export interface WaitingDaysStackedBarChartProps extends ChartRendererProps {}

interface WaitingDaysSegment {
    bucket: string;
    label: string;
    value: number;
    color: string;
}

const segmentOpacity = (hoveredIndex: number | null, index: number): number => {
    if (hoveredIndex === null || hoveredIndex === index) {
        return 1;
    }

    return 0.25;
};

const tooltipOffsetPercent = (segments: WaitingDaysSegment[], hoveredIndex: number, total: number): number => {
    const start = segments.slice(0, hoveredIndex).reduce((sum, segment) => sum + segment.value, 0);

    return ((start + segments[hoveredIndex].value / 2) / total) * 100;
};

export const WaitingDaysStackedBarChart = ({ data, className, segmentLabels, onSegmentClick }: WaitingDaysStackedBarChartProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const segments = useMemo(() => {
        const grouped = toWaitingDaysDonutChart(data).series[0]?.data ?? [];

        return WAITING_DAYS_DONUT_LABELS.map((bucket, index) => ({
            bucket,
            label: segmentLabels?.[index] ?? bucket,
            value: grouped.find((point) => point.label === bucket)?.value ?? 0,
            color: WAITING_DAYS_STACKED_BAR_COLORS[index]
        }));
    }, [data, segmentLabels]);

    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    const hoveredSegment = hoveredIndex !== null ? segments[hoveredIndex] : null;

    const handleSegmentClick = (bucket: string) => (event: MouseEvent) => {
        if (!onSegmentClick) {
            return;
        }
        event.stopPropagation();
        onSegmentClick(bucket);
    };

    return (
        <div className={`flex h-full min-h-0 w-full flex-col justify-start gap-3 ${className ?? ''}`}>
            <div className='relative w-full shrink-0'>
                {hoveredIndex !== null && hoveredSegment && hoveredSegment.value > 0 && total > 0 && (
                    <div
                        className='bg-background-surface outline-border-default text-foreground-default pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 rounded-md px-2 py-1 text-xs font-semibold outline-1'
                        style={{ left: `${tooltipOffsetPercent(segments, hoveredIndex, total)}%` }}>
                        {hoveredSegment.label}: {hoveredSegment.value}
                    </div>
                )}
                <div className='bg-border-default flex h-2 w-full overflow-hidden rounded-full'>
                    {total > 0 ? (
                        segments.map((segment, index) =>
                            segment.value > 0 ? (
                                <div
                                    key={segment.bucket}
                                    className='h-full min-w-0 cursor-pointer transition-opacity'
                                    style={{
                                        width: `${(segment.value / total) * 100}%`,
                                        backgroundColor: segment.color,
                                        opacity: segmentOpacity(hoveredIndex, index)
                                    }}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    onClick={handleSegmentClick(segment.bucket)}
                                />
                            ) : null
                        )
                    ) : (
                        <div className='bg-border-default h-full w-full rounded-full' />
                    )}
                </div>
            </div>
            <div className='flex shrink-0 flex-col items-start justify-start gap-1'>
                {segments.map((segment, index) => (
                    <div
                        key={segment.bucket}
                        className='flex cursor-pointer items-center gap-2'
                        style={{ opacity: segmentOpacity(hoveredIndex, index) }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={handleSegmentClick(segment.bucket)}>
                        <span
                            className='size-2.5 shrink-0 rounded-full'
                            style={{ backgroundColor: segment.color }}
                        />
                        <span className='text-foreground-default text-sm font-medium'>{segment.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
